import { Tag, Tooltip } from "antd"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import {
  ICurrencyInfo,
  ICurrencyInfoRes,
  IPaymentScheduleItem,
  IPayoutsResponse,
  IPayScheduleItemDocsEntity,
  IPayScheduleItemRes,
} from "./interface/payouts"
import { formatCurrencyNoSymbol } from "utils/currency"
import { useGet } from "utils/useFetch"
import getIdFromKey from "utils/getIdFromKey"
import { useLocation } from "react-router-dom"
import { ColumnsType } from "antd/es/table"
import { availableFlags } from "utils/flags"
import { antIcon } from "pages/dashboard/dashboard/Dashboard"
import { useAuthStore } from "stores"
import { useState } from "react"
import DowntimeModal from "pages/employee-dashboard/wallet/components/DowntimeModal"
import {
  PAYMENT_SCHEDULES_ENDPOINTS,
  paymentSchedulesService,
} from "services/paymentSchedulesService"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"
import { AppTable } from "components/new-stuff/app-table"

const ViewGlobal = ({
  currencies,
  activeCurrency,
}: {
  currencies: ICurrencyInfoRes | undefined
  activeCurrency?: ICurrencyInfo | null
}) => {
  const location = useLocation()
  const myParam = location.search
  const { user } = useAuthStore()
  const x = new URLSearchParams(myParam).get("x")
  const {
    isLoading: isPayoutLoading,
    data: payoutData,
    handlePaginationChange,
  } = useBackendTableQuery<IPayScheduleItemRes>({
    queryKeyBase: [PAYMENT_SCHEDULES_ENDPOINTS.SELECT_PAYOUT_ITEMS],
    queryFn: (params) => paymentSchedulesService.selectPayoutItems(params),
    getTotal: (response) => response?.data?.totalDocs || 0,
    searchParamName: "searchParams",
    initialPageSize: 10,
    initialExtraParams: {
      id: getIdFromKey(x || ""),
    },
  })

  const [showDownTimeModal, setShowDownTimeModal] = useState(
    user?.paymentProviderStatus === "DOWN" ? true : false
  )
  const uniqueBankNames = [
    ...new Set(payoutData?.data?.docs?.map((pay: any) => pay.bank_name)),
  ]
  const uniqueStatusNames = [
    ...new Set(
      payoutData?.data?.docs?.map(
        (pay: any) => pay.payment_schedule_item_status
      )
    ),
  ]
  const normalizedBankNames = uniqueBankNames?.map(
    (bankName) => bankName ?? "Unspecified"
  )
  const distinctBankNames = [...new Set(normalizedBankNames)]
  const sortedBankNames = distinctBankNames.sort((a, b) => {
    if (a === "Unspecified") return -1
    if (b === "Unspecified") return 1
    return a.localeCompare(b)
  })
  const bankFilters = sortedBankNames.map((bankName) => ({
    text: bankName,
    value: bankName,
  }))
  const statusFilters = uniqueStatusNames?.map((status) => ({
    text: status,
    value: status,
  }))
  const TableColumns: ColumnsType<IPayScheduleItemDocsEntity> = [
    {
      title: "Beneficiary",
      dataIndex: "beneficiary",
      render: (_, record: IPayScheduleItemDocsEntity) => (
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <span
              className={`font-circular flex flex-wrap text-sm text-padeBlack`}
            >
              {record?.beneficiary_name || record?.full_name}
            </span>
            {Number(record.payment_schedule_item_type_id) > 1 && (
              <Tag
                color="processing"
                className="w-fit text-xs font-medium"
              >
                {record.payment_schedule_item_type_id == 3
                  ? "Pension Schedule"
                  : record.payment_schedule_item_type_id == 4
                  ? "NHF Schedule"
                  : record.payment_schedule_item_type_id == 6
                  ? "NHIS Schedule"
                  : record.payment_schedule_item_type_id == 2
                  ? "Tax Schedule"
                  : record.payment_schedule_item_type_id == 8
                  ? "EWA Repayment"
                  : record.payment_schedule_item_type_id == 9
                  ? "NSITF Schedule"
                  : record.payment_schedule_item_type_id == 11
                  ? "Subscription Fee"
                  : record.payment_schedule_item_type_id == 12
                  ? "External Loan Repayment"
                  : record.payment_schedule_item_type_id == 13
                  ? "Subscription VAT Fee"
                  : ""}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Account",
      dataIndex: "bank",
      width: "25%",
      filters: bankFilters,
      onFilter: (value: boolean | Key, record: IPayScheduleItemDocsEntity) => {
        const recordBank = record?.bank_name ?? "Unspecified"
        return recordBank === value
      },
      render: (_, record: IPayScheduleItemDocsEntity) =>
        record?.source_currency_code === "USD" && record?.account_number ? (
          <div className="flex flex-col mp-mask">
            <span
              className={`font-circular flex flex-wrap text-sm text-padeBlack`}
            >
              {record?.routing_code}
              {record?.routing_type ? `(${record?.routing_type})` : "-"}
            </span>
            <span
              className={`font-circular flex flex-wrap text-xs text-padeBlack`}
            >
              {record.account_number}
            </span>
          </div>
        ) : record?.currency_code === "NGN" && record?.bank_name ? (
          <div className="flex flex-col mp-mask">
            <span
              className={`font-circular flex flex-wrap text-sm text-padeBlack`}
            >
              {record?.bank_name}
            </span>
            <span
              className={`font-circular flex flex-wrap text-xs text-padeBlack`}
            >
              {record.account_number}
            </span>
          </div>
        ) : record?.currency_code !== "NGN" && record?.account_number ? (
          <div className="flex flex-col mp-mask">
            <span
              className={`font-circular flex flex-wrap text-sm text-padeBlack`}
            >
              {record?.routing_code}
              {record?.routing_type ? `(${record?.routing_type})` : "-"}
            </span>
            <span
              className={`font-circular flex flex-wrap text-xs text-padeBlack`}
            >
              {record.account_number ?? "-"}
            </span>
          </div>
        ) : (
          <Tooltip title="Employee is missing details required to run payroll. ">
            <div className="flex gap-2 items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-danger500" />
              <span>Unspecified</span>
            </div>
          </Tooltip>
        ),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      filters:
        currencies?.data?.map((item) => ({
          text: item.currency_code,
          value: item.currency_code,
        })) || [],
      onFilter: (value, record) => {
        return record?.currency_code === value
      },
      render: (_, record: IPayScheduleItemDocsEntity) => {
        const flag = availableFlags?.find(
          (item) => item?.code === record?.currency_code
        )?.flag
        return (
          <div className="flex items-center gap-2">
            <div>
              <img
                src={flag}
                alt=""
                className="h-6 w-6"
              />
            </div>
            <span
              className={`font-circular flex flex-wrap text-sm text-padeBlack`}
            >
              {record?.currency_code}
            </span>
          </div>
        )
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_, record: IPayScheduleItemDocsEntity) => (
        <div className="flex flex-col">
          <span
            className={`whitespace-nowrap font-avenir text-sm text-padeBlack mp-mask`}
          >
            {record?.source_currency_code == "USD"
              ? "$"
              : record?.currency_symbol ?? (
                  <ExclamationTriangleIcon className="h-6 w-6 text-danger500" />
                )}
            {formatCurrencyNoSymbol(Number(record.amount))}
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      filters: statusFilters,
      onFilter: (value: boolean | Key, record: IPayScheduleItemDocsEntity) => {
        const filterValue = String(value)
        return record?.payment_schedule_item_status === filterValue
      },
      render: (_, record: IPayScheduleItemDocsEntity) => (
        <div className="">
          <div className="flex items-center gap-1">
            <Tag
              color={
                record.payment_schedule_item_status_id === 1
                  ? "green"
                  : record.payment_schedule_item_status_id === 2
                  ? "orange"
                  : record.payment_schedule_item_status_id === 3
                  ? "red"
                  : record.payment_schedule_item_status_id === 4
                  ? "red"
                  : "default"
              }
            >
              {record?.payment_schedule_item_status}
            </Tag>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="">
      <div className=" flex flex-row gap-[11px]  w-full overflow-auto scrollbar-hide">
        {currencies?.data?.map((item, index) => (
          <div
            key={index}
            className="h-[89px] w-[262px] bg-white rounded-5px px-8 text-[24px] text-padeBlack font-circular flex items-center mp-mask"
          >
            {item?.currency_symbol}
            {formatCurrencyNoSymbol(Number(item?.total))}
          </div>
        ))}
      </div>
      <div className="mt-[26px]">
        <AppTable
          // rowSelection={{
          //   type: "checkbox",
          // }}
          columns={TableColumns}
          dataSource={payoutData?.data?.docs || []}
          rowKey="payment_schedule_item_id"
          loading={{ spinning: isPayoutLoading, indicator: antIcon }}
          className="text-[#42526D]"
          pagination={{
            showTotal: (total, range) => (
              <span className="font-avenir text-padeSubtext grow">
                {range?.[0]}-{range?.[1]} of {total} items
              </span>
            ),
            pageSize: payoutData?.data?.limit,
            current: payoutData?.data?.page,
            total: payoutData?.data?.totalDocs,
            showSizeChanger: true,
            pageSizeOptions: [20, 40, 100, 500, 1000],
          }}
          onChange={(pagination) => {
            handlePaginationChange(pagination.current!, pagination.pageSize)
          }}
        />
      </div>
      <DowntimeModal
        open={showDownTimeModal}
        onClose={() => setShowDownTimeModal(false)}
      />
    </div>
  )
}

export default ViewGlobal
type Key = string | number | symbol | bigint
